import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { MentionUser } from './mentionSuggestion';

export type MentionListProps = {
  items: MentionUser[];
  command: (attrs: { id: string; label: string }) => void;
};

export type MentionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

/** The `@` suggestion popup. Rendered by `ReactRenderer` inside a tippy instance. */
const MentionList = forwardRef<MentionListRef, MentionListProps>(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    setSelected(0);
  }, [items]);

  const pick = (index: number) => {
    const item = items[index];
    if (item) {
      command({ id: item.id, label: item.label });
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (!items.length) {
          return false;
        }
        if (event.key === 'ArrowUp') {
          setSelected((current) => (current + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelected((current) => (current + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          pick(selected);
          return true;
        }
        return false;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, selected],
  );

  if (!items.length) {
    return null;
  }

  return (
    <div className="mention-list">
      {items.map((item, index) => (
        <button
          type="button"
          key={item.id}
          className={`mention-list-item${index === selected ? ' is-selected' : ''}`}
          onMouseEnter={() => setSelected(index)}
          onMouseDown={(event) => {
            event.preventDefault();
            pick(index);
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
});

MentionList.displayName = 'MentionList';

export default MentionList;
